import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

export default function VoiceSettingsPanel({ userId, isOpen, onClose }) {
  // Voice settings state
  const [voiceMode, setVoiceMode] = useState('standard')
  const [micVolume, setMicVolume] = useState(100)
  const [noiseLevel, setNoiseLevel] = useState(50)
  const [echoCancellation, setEchoCancellation] = useState(true)
  const [autoGain, setAutoGain] = useState(true)
  const [attenuationLevel, setAttenuationLevel] = useState(0)
  const [currentLevel, setCurrentLevel] = useState(-20)
  const [videoQuality, setVideoQuality] = useState('hd')

  // Audio processing refs
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const streamRef = useRef(null)
  const animationRef = useRef(null)

  // Load settings from database
  useEffect(() => {
    if (userId && isOpen) {
      loadSettings()
    }
  }, [userId, isOpen])

  // Initialize audio monitoring when opened
  useEffect(() => {
    if (isOpen) {
      initAudioMonitoring()
    } else {
      cleanupAudioMonitoring()
    }

    return cleanupAudioMonitoring
  }, [isOpen])

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('voice_settings')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error) {
        // Initialize settings if they don't exist
        await supabase.from('voice_settings').insert({
          user_id: userId,
          microphone_volume: 100,
          voice_mode: 'standard',
          noise_suppression_enabled: true,
          noise_suppression_level: 50,
          echo_cancellation_enabled: true,
          auto_gain_enabled: true,
          attenuation_level: 0,
          video_quality: 'hd'
        })
      } else if (data) {
        setVoiceMode(data.voice_mode)
        setMicVolume(data.microphone_volume)
        setNoiseLevel(data.noise_suppression_level)
        setEchoCancellation(data.echo_cancellation_enabled)
        setAutoGain(data.auto_gain_enabled)
        setAttenuationLevel(data.attenuation_level)
        setVideoQuality(data.video_quality)
      }
    } catch (err) {
      console.error('Error loading voice settings:', err)
    }
  }

  const initAudioMonitoring = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation,
          noiseSuppression: voiceMode !== 'off',
          autoGainControl: autoGain
        }
      })

      streamRef.current = stream

      // Create audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      audioContextRef.current = audioContext

      // Create analyser node
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      analyserRef.current = analyser

      // Connect stream to analyser
      const source = audioContext.createMediaStreamSource(stream)
      source.connect(analyser)

      // Start monitoring
      monitorAudioLevel()
    } catch (err) {
      console.error('Error initializing audio:', err)
    }
  }

  const monitorAudioLevel = () => {
    if (!analyserRef.current) return

    const analyser = analyserRef.current
    const dataArray = new Uint8Array(analyser.frequencyBinCount)

    const checkLevel = () => {
      analyser.getByteFrequencyData(dataArray)
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length

      // Convert to dB scale (-20 to +20)
      const dbLevel = average > 0
        ? Math.min(20, Math.max(-20, 20 * Math.log10(average / 128)))
        : -20

      setCurrentLevel(dbLevel + attenuationLevel)
      animationRef.current = requestAnimationFrame(checkLevel)
    }

    checkLevel()
  }

  const cleanupAudioMonitoring = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
    }
  }

  const getLevelColor = (level) => {
    if (level < -10) return '#ef4444' // Red - too quiet
    if (level < 0) return '#f59e0b' // Yellow - good
    if (level < 10) return '#10b981' // Green - optimal
    return '#f97316' // Orange - loud
  }

  const saveSettings = async (updates) => {
    try {
      await supabase
        .from('voice_settings')
        .upsert({
          user_id: userId,
          ...updates
        })
    } catch (err) {
      console.error('Error saving settings:', err)
    }
  }

  const handleVoiceModeChange = (newMode) => {
    setVoiceMode(newMode)
    saveSettings({ voice_mode: newMode })

    // Restart audio with new mode
    cleanupAudioMonitoring()
    setTimeout(initAudioMonitoring, 100)
  }

  const handleMicVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value)
    setMicVolume(newVolume)
    saveSettings({ microphone_volume: newVolume })
  }

  const handleNoiseLevelChange = (e) => {
    const newLevel = parseInt(e.target.value)
    setNoiseLevel(newLevel)
    saveSettings({ noise_suppression_level: newLevel })
  }

  const handleEchoToggle = (e) => {
    const newValue = e.target.checked
    setEchoCancellation(newValue)
    saveSettings({ echo_cancellation_enabled: newValue })

    // Restart audio with new settings
    cleanupAudioMonitoring()
    setTimeout(initAudioMonitoring, 100)
  }

  const handleAutoGainToggle = (e) => {
    const newValue = e.target.checked
    setAutoGain(newValue)
    saveSettings({ auto_gain_enabled: newValue })

    // Restart audio with new settings
    cleanupAudioMonitoring()
    setTimeout(initAudioMonitoring, 100)
  }

  const handleAttenuationChange = (e) => {
    const newLevel = parseInt(e.target.value)
    setAttenuationLevel(newLevel)
    saveSettings({ attenuation_level: newLevel })
  }

  const handleVideoQualityChange = (e) => {
    const newQuality = e.target.value
    setVideoQuality(newQuality)
    saveSettings({ video_quality: newQuality })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      {/* Clickable backdrop to close modal */}
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-gjyl-slate-900/95 backdrop-blur-gjyl border-2 border-gjyl-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden animate-scale-in shadow-gjyl-lift-strong">
        {/* Header */}
        <div className="bg-gjyl-slate-800/95 backdrop-blur-gjyl p-6 border-b border-gjyl-slate-700">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">Voice & Video Settings</h2>
            <button
              onClick={onClose}
              className="btn-icon"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8 overflow-y-auto max-h-[calc(80vh-96px)]">
          {/* Voice Mode Selector */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Voice Mode</h3>
            <select
              value={voiceMode}
              onChange={(e) => handleVoiceModeChange(e.target.value)}
              className="w-full p-3 glass rounded-gjyl text-white focus:ring-2 focus:ring-gjyl-indigo-500 outline-none"
            >
              <option value="off">Off - No Processing (Raw Audio)</option>
              <option value="standard">Standard - Balanced Processing</option>
              <option value="isolation">Voice Isolation - Suppress Background</option>
              <option value="music">Music Mode - For Instruments</option>
            </select>

            <div className="text-sm text-slate-400 space-y-1">
              {voiceMode === 'off' && (
                <p>• Raw microphone input with no processing. Perfect for testing.</p>
              )}
              {voiceMode === 'standard' && (
                <p>• Balanced settings for normal voice chat. Moderate noise suppression.</p>
              )}
              {voiceMode === 'isolation' && (
                <p>• Maximum noise suppression focusing on human voice (300Hz-3400Hz). Ideal for noisy environments.</p>
              )}
              {voiceMode === 'music' && (
                <p>• Full frequency range (20Hz-20kHz) for instruments. No voice-specific filtering. Perfect for guitar, piano, singing.</p>
              )}
            </div>
          </div>

          {/* Attenuation Gauge */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Microphone Level Monitor</h3>
            <div className="space-y-2">
              <div className="h-8 glass rounded-gjyl-full overflow-hidden relative">
                <div
                  className="h-full transition-all duration-150"
                  style={{
                    width: `${((currentLevel + 20) / 40) * 100}%`,
                    backgroundColor: getLevelColor(currentLevel)
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-slate-400">
                <span>-20 dB</span>
                <span className="font-semibold" style={{ color: getLevelColor(currentLevel) }}>
                  {currentLevel.toFixed(1)} dB
                </span>
                <span>+20 dB</span>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              🔴 Red: Too quiet | 🟡 Yellow: Good | 🟢 Green: Optimal | 🟠 Orange: Loud
            </p>
          </div>

          {/* Attenuation Adjustment */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Attenuation Level</h3>
              <span className="text-sm font-mono text-gjyl-indigo-400">
                {attenuationLevel > 0 ? '+' : ''}{attenuationLevel} dB
              </span>
            </div>
            <input
              type="range"
              min="-20"
              max="20"
              value={attenuationLevel}
              onChange={handleAttenuationChange}
              className="w-full h-2 glass rounded-gjyl-full appearance-none cursor-pointer
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:w-6
                       [&::-webkit-slider-thumb]:h-6
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-gjyl-indigo-500
                       [&::-webkit-slider-thumb]:shadow-gjyl-glow
                       [&::-webkit-slider-thumb]:cursor-pointer
                       [&::-webkit-slider-thumb]:hover:scale-110
                       [&::-webkit-slider-thumb]:transition-transform"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>Quieter</span>
              <span>Normal</span>
              <span>Louder</span>
            </div>
          </div>

          {/* Microphone Volume */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Microphone Volume</h3>
              <span className="text-sm font-mono text-gjyl-indigo-400">{micVolume}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={micVolume}
              onChange={handleMicVolumeChange}
              className="w-full h-2 glass rounded-gjyl-full appearance-none cursor-pointer
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:w-6
                       [&::-webkit-slider-thumb]:h-6
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-gjyl-indigo-500
                       [&::-webkit-slider-thumb]:shadow-gjyl-glow
                       [&::-webkit-slider-thumb]:cursor-pointer
                       [&::-webkit-slider-thumb]:hover:scale-110
                       [&::-webkit-slider-thumb]:transition-transform"
            />
          </div>

          {/* Noise Suppression */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Noise Suppression</h3>
              <span className="text-sm font-mono text-gjyl-indigo-400">{noiseLevel}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={noiseLevel}
              onChange={handleNoiseLevelChange}
              disabled={voiceMode === 'off'}
              className="w-full h-2 glass rounded-gjyl-full appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:w-6
                       [&::-webkit-slider-thumb]:h-6
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-gjyl-indigo-500
                       [&::-webkit-slider-thumb]:shadow-gjyl-glow
                       [&::-webkit-slider-thumb]:cursor-pointer
                       [&::-webkit-slider-thumb]:hover:scale-110
                       [&::-webkit-slider-thumb]:transition-transform"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>Off</span>
              <span>Low</span>
              <span>Medium</span>
              <span>High</span>
              <span>Max</span>
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Audio Processing</h3>

            <label className="flex items-center justify-between p-4 glass rounded-gjyl cursor-pointer hover:glass-strong transition-all">
              <div className="flex-1">
                <div className="font-medium text-white">Echo Cancellation</div>
                <div className="text-sm text-slate-400">Removes echo and feedback</div>
              </div>
              <input
                type="checkbox"
                checked={echoCancellation}
                onChange={handleEchoToggle}
                disabled={voiceMode === 'off'}
                className="w-12 h-6 appearance-none rounded-gjyl-full glass relative cursor-pointer transition-all
                         checked:bg-gjyl-indigo-500
                         before:content-[''] before:absolute before:w-5 before:h-5 before:rounded-full
                         before:bg-white before:top-0.5 before:left-0.5
                         before:transition-transform before:shadow-md
                         checked:before:translate-x-6
                         disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </label>

            <label className="flex items-center justify-between p-4 glass rounded-gjyl cursor-pointer hover:glass-strong transition-all">
              <div className="flex-1">
                <div className="font-medium text-white">Auto Gain Control</div>
                <div className="text-sm text-slate-400">Automatically adjusts microphone sensitivity</div>
              </div>
              <input
                type="checkbox"
                checked={autoGain}
                onChange={handleAutoGainToggle}
                disabled={voiceMode === 'off'}
                className="w-12 h-6 appearance-none rounded-gjyl-full glass relative cursor-pointer transition-all
                         checked:bg-gjyl-indigo-500
                         before:content-[''] before:absolute before:w-5 before:h-5 before:rounded-full
                         before:bg-white before:top-0.5 before:left-0.5
                         before:transition-transform before:shadow-md
                         checked:before:translate-x-6
                         disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </label>
          </div>

          {/* Video Quality */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Video Quality</h3>
            <select
              value={videoQuality}
              onChange={handleVideoQualityChange}
              className="w-full p-3 glass rounded-gjyl text-white focus:ring-2 focus:ring-gjyl-indigo-500 outline-none"
            >
              <option value="sd">SD - 480p (Data Saver)</option>
              <option value="hd">HD - 720p (Recommended)</option>
              <option value="fhd">Full HD - 1080p (High Quality)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  )
}
